/* eslint-disable max-lines-per-function */
import './CoralPageHero.scss'

import { useIsWindowBiggerThan } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { Baseplate, StudRange } from '@websrc/App/Components/Baseplate/Baseplate'
import { BrickColor } from '@websrc/App/Components/Brick/Brick'
import { CTA } from '@websrc/App/Components/CTA'
import { Markdown } from '@websrc/App/Components/Markdown/Markdown'
import { useSignUpVisibility } from '@websrc/App/Hooks/useSignUpVisibility'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapCoralWebHero } from '@websrc/DataMappers/mapHero'
import clsx from 'clsx'
import React, { FC, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

type CoralPageHeroProps = ReturnType<typeof mapCoralWebHero>

export const CoralPageHero: FC<CoralPageHeroProps> = ({ header, description, asset, sign_up }) => {
  const { matomoPageTemplate } = useContext(AppProviderContext)
  const { showTopbarSignUpButton, hideTopbarSignUpButton } = useSignUpVisibility()
  const [isPlaying, setIsPlaying] = useState(false)
  const signUpButtonRef = useRef<HTMLAnchorElement | HTMLButtonElement | null>(null)

  const isTablet = useIsWindowBiggerThan(719)
  const isCustomTablet = useIsWindowBiggerThan(899)
  const isDesktop = useIsWindowBiggerThan(1079)
  const isWideScreen = useIsWindowBiggerThan(1439)

  const studRangeAsset = useMemo((): StudRange[] => {
    if (isWideScreen) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 16, columns: 30 },
          offset: { right: 1 },
          bricks: [
            { row: 2, column: 28, color: BrickColor.WarmYellow, snapPosition: 'bottom-left' },
            { row: 4, column: 29, color: BrickColor.Coral, snapPosition: 'bottom-left' },
          ],
        },
      ]
    }
    if (isDesktop) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 14, columns: 28 },
          bricks: [
            { row: 2, column: 25, color: BrickColor.Sand, snapPosition: 'bottom-left' },
            { row: 4, column: 26, color: BrickColor.Coral, snapPosition: 'bottom-left' },
          ],
        },
      ]
    } else if (isCustomTablet) {
      return [
        {
          snapPosition: 'top-right',
          size: { rows: 20, columns: 3 },
          offset: { top: 2, right: 2 },
          bricks: [
            { row: 2, column: 4, color: BrickColor.Coral, snapPosition: 'bottom-right' },
            { row: 0, column: 3, color: BrickColor.Sand, snapPosition: 'bottom-right' },
          ],
        },
      ]
    } else if (isTablet) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 20, columns: 3 },
          offset: { top: 2, left: 2 },
        },
        {
          snapPosition: 'top-right',
          size: { rows: 20, columns: 3 },
          offset: { top: 2, right: 2 },
          bricks: [
            { row: 2, column: 4, color: BrickColor.Coral, snapPosition: 'bottom-right' },
            { row: 0, column: 3, color: BrickColor.Sand, snapPosition: 'bottom-right' },
          ],
        },
      ]
    } else {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: 11, columns: 1 },
        },
        {
          snapPosition: 'top-right',
          size: { rows: 11, columns: 1 },
          bricks: [{ row: 1, column: 1, color: BrickColor.Coral, snapPosition: 'bottom-right' }],
        },
      ]
    }
  }, [isCustomTablet, isDesktop, isTablet, isWideScreen])

  // useLayoutEffect instead of useEffect to ensure the ref is set before the effect runs
  useLayoutEffect(() => {
    const signUpButtonElement = signUpButtonRef.current
    if (signUpButtonElement) {
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              hideTopbarSignUpButton()
            } else {
              showTopbarSignUpButton()
            }
          })
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 1.0,
        }
      )

      intersectionObserver.observe(signUpButtonElement)

      return () => intersectionObserver.unobserve(signUpButtonElement)
    }
  })

  useEffect(() => {
    const handleVideoPlaying = (mutations: MutationRecord[]) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement
          if (target.className.includes('playing')) {
            setIsPlaying(true)
          } else {
            setIsPlaying(false)
          }
        }
      })
    }

    const observer = new MutationObserver(handleVideoPlaying)
    const button = document.querySelector('.coral-page-hero__image button[aria-label="play"]')

    if (button) {
      observer.observe(button, { attributes: true })
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <div className={'coral-page-hero-container'}>
      <div style={isWideScreen ? { gridColumn: '1 / 3', gridRow: '1 / 3' } : {}}>
        <div className={'coral-page-hero__heading-container'}>
          <p className={'coral-page-hero__label'}>{header.label}</p>
          <h1 className={'coral-page-hero__header'}>{header.headline}</h1>
        </div>
        <div className={'coral-page-hero__intro-section'}>
          <Baseplate
            className={clsx('coral-page-hero__image-container', isPlaying && 'm--playing')}
            studRanges={studRangeAsset}
          >
            <div className={'coral-page-hero__background'} />
            <div className={'coral-page-hero__image-wrapper'}>
              <Asset
                altText={asset?.altText}
                className={'coral-page-hero__image'}
                objectFit={'cover'}
                thumbnail={asset?.thumbnail}
                video={asset?.video}
              />
            </div>
          </Baseplate>

          <Markdown
            className={clsx(
              'coral-page-hero__text',
              sign_up.button?.url && sign_up.fine_copy && 'm--align-start'
            )}
            markdown={description ?? ''}
          />
          <div
            className={clsx(
              'coral-page-hero__cta-container',
              sign_up.button?.url && sign_up.fine_copy && 'm--no-button'
            )}
          >
            {sign_up.button?.text && (
              <CTA
                className={'coral-page-hero__cta'}
                cta={sign_up.button}
                onClick={() => {
                  matomo.trackEvent(
                    'conversion',
                    matomoPageTemplate,
                    ['coral page hero'],
                    ['coral sign up button'],
                    'button'
                  )
                }}
                ref={signUpButtonRef as React.RefObject<HTMLAnchorElement>}
              />
            )}
            {sign_up.fine_copy && (
              <p className={'coral-page-hero__availability'}>
                <span className={'coral-page-hero__availability__label'}>{sign_up.fine_copy}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
